import React from 'react'
import { isChunkLoadError } from '../lib/lazyWithReload'

type ErrorBoundaryProps = {
  children: React.ReactNode
  fallback?: React.ReactNode
}

type ErrorBoundaryState = {
  hasError: boolean
  error?: any
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: any): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: any, errorInfo: any) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      // lazyWithReload already reloaded once and it failed again (or storage is blocked). «Повторить»
      // can't help here — React.lazy caches the rejected import — so offer a real page reload and
      // don't show the raw browser message with a file URL.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="min-h-[200px] p-6 bg-slate-50 border border-slate-200 rounded-xl text-slate-800">
            <div className="font-semibold mb-2">Сайт обновился</div>
            <div className="text-sm mb-4">
              Мы выпустили новую версию, и эта вкладка ещё открыта со старой. Обновите страницу — все данные на месте.
            </div>
            <button
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-700"
              onClick={() => window.location.reload()}
            >
              Обновить страницу
            </button>
          </div>
        )
      }
      return (
        <div className="min-h-[200px] p-6 bg-red-50 border border-red-200 rounded-xl text-red-800">
          <div className="font-semibold mb-2">Не удалось показать этот раздел</div>
          <div className="text-sm mb-4 break-all">
            {String(this.state.error?.message || this.state.error || 'Неизвестная ошибка')}
          </div>
          <button
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            onClick={this.handleReset}
          >
            Повторить
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
